import type { Todo, UpdateTodoBody } from '@kanto/shared';
import { useEffect, useRef, useState, type ReactElement, type TouchEvent } from 'react';
import { formatDue, isOverdue, isToday } from '../lib/dates';
import { useT } from '../lib/i18n';
import { TagChips } from './TagChips';

/**
 * One todo: a box to tick, what it says, and when it is due.
 *
 * The actions are hidden until they are wanted. On a pointer they appear on hover;
 * on a touch screen, where there is no hover, the row slides aside under a swipe.
 * A list where every row carries two permanent buttons is a list where the buttons
 * are what you read first.
 *
 * The title is an input at all times rather than a label that becomes one. Swapping
 * the element on click loses the caret position — the click lands on text and the
 * caret arrives at the end of it — and an input styled to look like text costs
 * nothing else.
 */

/** How far the row slides to uncover its actions, in pixels. */
const REVEAL = 96;

export function TodoRow({
  todo,
  onUpdate,
  onArchive,
  highlighted = false,
}: {
  todo: Todo;
  onUpdate: (patch: UpdateTodoBody) => void;
  onArchive: () => void;
  /** Named by a search hit: this is the row somebody chose in the palette. */
  highlighted?: boolean;
}): ReactElement {
  const t = useT();
  const [revealed, setRevealed] = useState(false);
  const swipe = useRef<{ x: number; y: number; horizontal: boolean } | null>(null);
  const row = useRef<HTMLLIElement | null>(null);

  // Brought into view rather than merely tinted: a search hit that lands below
  // the fold of a forty-row list is a hit nobody sees.
  useEffect(() => {
    if (highlighted) row.current?.scrollIntoView({ block: 'center' });
  }, [highlighted]);

  const done = todo.doneAt !== null;

  /**
   * The draft title lives in the DOM rather than in state, so there is nothing to
   * keep in step with the server: `key={todo.title}` remounts the field when a
   * different title arrives — from another device, or from a rollback — and while
   * somebody is typing that value has not changed, so nothing moves under them.
   */
  const commitTitle = (input: HTMLInputElement): void => {
    const next = input.value.trim();
    // Nothing typed, or nothing changed: an empty title is refused by the server,
    // and the row would look blank until the refusal arrived.
    if (!next || next === todo.title) {
      input.value = todo.title;
      return;
    }
    onUpdate({ title: next });
  };

  /**
   * A horizontal drag reveals the actions; a vertical one is the page scrolling
   * and must be left alone. The direction is decided once, on the first few
   * pixels, because a gesture that changes its mind halfway is one that scrolls
   * the page sideways by accident.
   */
  const onTouchStart = (event: TouchEvent): void => {
    const touch = event.touches[0];
    if (touch) swipe.current = { x: touch.clientX, y: touch.clientY, horizontal: false };
  };

  const onTouchMove = (event: TouchEvent): void => {
    const start = swipe.current;
    const touch = event.touches[0];
    if (!start || !touch) return;

    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    if (!start.horizontal && Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy)) {
      start.horizontal = true;
    }
    if (start.horizontal) setRevealed(dx < -12);
  };

  const onTouchEnd = (): void => {
    swipe.current = null;
  };

  const due = todo.dueOn;
  const dueTone = !due
    ? ''
    : isOverdue(due)
      ? 'text-alert'
      : isToday(due)
        ? 'text-accent'
        : 'text-ink-400';

  return (
    <li ref={row} className="group relative overflow-hidden">
      {/* Underneath the row, uncovered by the swipe. Hidden from assistive
          technology and from the keyboard: the same actions sit in the row
          itself above, and offering each of them twice would double the length
          of the list to anybody using either. */}
      <div className="absolute inset-y-0 right-0 flex items-center pr-4 lg:hidden" aria-hidden>
        <button
          type="button"
          tabIndex={-1}
          onClick={onArchive}
          className="flex min-h-11 items-center gap-2 rounded-lg px-3 text-sm font-medium text-alert"
        >
          <ArchiveIcon />
        </button>
      </div>

      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{ transform: revealed ? `translateX(-${REVEAL}px)` : undefined }}
        // `touch-action: pan-y` hands vertical scrolling back to the browser, so
        // the handlers above never have to fight it — the one part of a swipe
        // that cannot be got right in JavaScript.
        // `relative` so the overlay below travels with the row when it is swiped
        // aside, rather than staying anchored to the list item underneath.
        className={`relative flex touch-pan-y items-start gap-3 px-4 py-3 transition-transform duration-200 ${
          highlighted ? 'bg-accent-soft' : 'bg-surface'
        }`}
      >
        <input
          type="checkbox"
          checked={done}
          onChange={(event) => onUpdate({ done: event.target.checked })}
          aria-label={t(done ? 'todos.markOpen' : 'todos.markDone', todo.title)}
          // The native control, in the accent colour. A box redrawn from scratch
          // would have to reimplement the focus ring, the indeterminate state and
          // the way a screen reader announces it, for a tick of a different shape.
          className="mt-1 size-5 shrink-0 accent-accent"
        />

        <div className="min-w-0 flex-1">
          <input
            type="text"
            key={todo.title}
            defaultValue={todo.title}
            onBlur={(event) => commitTitle(event.currentTarget)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') event.currentTarget.blur();
              if (event.key === 'Escape') {
                event.currentTarget.value = todo.title;
                event.currentTarget.blur();
              }
            }}
            aria-label={t('todos.rename', todo.title)}
            className={`w-full bg-transparent text-base outline-none ${
              done ? 'text-ink-400 line-through' : 'text-ink-900'
            }`}
          />
          <TagChips tags={todo.tags} />
        </div>

        {due && <span className={`mt-0.5 shrink-0 text-sm ${dueTone}`}>{formatDue(due, t)}</span>}

        {/* The actions on a pointer, laid over the right of the row rather than
            beside it: reserved inline, their width would be a hand's breadth of
            empty space in every row of a list that is mostly one line long. They
            appear on `focus-within` as well as on hover, so a keyboard reaches
            them without a mouse ever moving. */}
        <div className="absolute inset-y-0 right-0 hidden items-center gap-1 bg-surface pr-3 pl-6 group-focus-within:flex group-hover:flex">
          <label className="sr-only" htmlFor={`due-${todo.id}`}>
            {t('todos.due')}
          </label>
          <input
            id={`due-${todo.id}`}
            type="date"
            value={todo.dueOn ?? ''}
            onChange={(event) => onUpdate({ dueOn: event.target.value || null })}
            className="min-h-9 rounded-lg border border-line bg-surface px-2 text-xs text-ink-500"
          />
          <button
            type="button"
            onClick={onArchive}
            aria-label={t('todos.archive', todo.title)}
            className="flex min-h-9 min-w-9 items-center justify-center rounded-lg text-ink-400 transition-colors hover:bg-alert-soft hover:text-alert"
          >
            <ArchiveIcon />
          </button>
        </div>
      </div>
    </li>
  );
}

function ArchiveIcon(): ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 7h18v3H3zM5 10v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9M10 14h4" />
    </svg>
  );
}
